/* variant-media-filter.js (Job 6 Mobile-First & Desktop Optimized)
   - Filters product media + thumbnail carousel items by Color variant
   - Robust ALT tag matching: "Color: Green - MotoTec ...", "Color: Blue", etc.
   - Listens to native changes, PubSub, and product-info:loaded
   - Synchronizes slider pagination & first visible slide
*/

(() => {
  const OPTION_NAME = 'Color';
  const DEBUG = false;

  const log = (...a) => DEBUG && console.log('[variant-media-filter]', ...a);
  const norm = (s) => (s || '').toString().trim().toLowerCase();

  // Parse "Color: Green" from alt text (supports "Color: Green - MotoTec...", "Color: Blue | ...", etc.)
  function parseColorFromAlt(alt) {
    const m = (alt || '').match(/color\s*:\s*([^|\-,;]+)/i);
    return m?.[1] ? norm(m[1]) : null;
  }

  // Detect selected color from select, radio pills, or swatch labels
  function getSelectedColor(scope = document) {
    const sel = scope.querySelector('select[name="options[' + OPTION_NAME + ']"], select[name*="Color"], select[name*="color"]');
    if (sel && sel.value) return norm(sel.value);

    const r =
      scope.querySelector('input[type="radio"][name="options[' + OPTION_NAME + ']"]:checked') ||
      scope.querySelector('input[type="radio"][name="' + OPTION_NAME + '"]:checked') ||
      scope.querySelector('input[type="radio"][name^="Color"]:checked') ||
      scope.querySelector('input[type="radio"][name*="Color"]:checked') ||
      scope.querySelector('input[type="radio"][name*="color"]:checked');
    if (r && r.value) return norm(r.value);

    const span = scope.querySelector('.product-form__input [data-selected-value]');
    if (span && span.textContent) return norm(span.textContent);

    return null;
  }

  function getRootFromTarget(t) {
    return t?.closest?.('.shopify-section') || document;
  }

  function ensureFlashStyle() {
    if (document.getElementById('vmf-style')) return;
    const style = document.createElement('style');
    style.id = 'vmf-style';
    style.textContent = `
      html.vmf-filtering .product__media-list,
      html.vmf-filtering .thumbnail-list,
      html.vmf-filtering .thumbnail-slider,
      html.vmf-filtering media-gallery {
        visibility: hidden !important;
      }
    `;
    document.head.appendChild(style);
  }

  function getMediaContainers(root) {
    const candidates = [
      ...root.querySelectorAll('[data-media-id]'),
      ...root.querySelectorAll('.product__media-item'),
      ...root.querySelectorAll('.product__media-list > li'),
      ...root.querySelectorAll('media-gallery li')
    ];
    return [...new Set(candidates)].filter(el => el.querySelector('img, video, model-viewer, iframe'));
  }

  function getThumbContainers(root) {
    const candidates = [
      ...root.querySelectorAll('.thumbnail-list__item'),
      ...root.querySelectorAll('[data-thumbnail-id]'),
      ...root.querySelectorAll('[data-target]'),
      ...root.querySelectorAll('.thumbnail-slider .slider__slide')
    ];
    return [...new Set(candidates)].filter(el => el.querySelector('img, button, a'));
  }

  function getAltFromContainer(el) {
    const img = el.querySelector('img[alt]');
    if (img) return img.getAttribute('alt') || '';
    return el.getAttribute('aria-label') || el.getAttribute('data-alt') || '';
  }

  function setHidden(el, hide) {
    if (!el) return;
    el.style.display = hide ? 'none' : '';
    el.toggleAttribute('hidden', !!hide);
    el.setAttribute('aria-hidden', hide ? 'true' : 'false');
    if (hide) {
      el.classList.add('is-filtered-out');
      el.classList.remove('is-active');
    } else {
      el.classList.remove('is-filtered-out');
    }
  }

  function refreshCarousels(root) {
    const sliders = root.querySelectorAll('slider-component');
    sliders.forEach(sl => {
      try {
        if (typeof sl.resetPages === 'function') sl.resetPages();
        if (typeof sl.initPages === 'function') sl.initPages();
        if (typeof sl.update === 'function') sl.update();
      } catch (e) {}
    });

    // Activate the first visible slide
    const firstVisibleSlide = root.querySelector('.product__media-list > .product__media-item:not([hidden]):not(.is-filtered-out)');
    if (firstVisibleSlide) {
      root.querySelectorAll('.product__media-list > .product__media-item').forEach(s => s.classList.remove('is-active'));
      firstVisibleSlide.classList.add('is-active');
      const targetId = firstVisibleSlide.getAttribute('data-media-id');

      // Update thumbnail active state
      if (targetId) {
        root.querySelectorAll('.thumbnail-list__item').forEach(th => {
          const btn = th.querySelector('button');
          if (btn) {
            const matches = th.getAttribute('data-target') === targetId;
            btn.setAttribute('aria-current', matches ? 'true' : 'false');
          }
        });
      }
    }

    window.dispatchEvent(new Event('resize'));
  }

  function filterContainers(containers, selected) {
    let tagged = 0;
    containers.forEach(el => {
      const alt = getAltFromContainer(el);
      const tag = parseColorFromAlt(alt);

      if (!tag) {
        setHidden(el, false);
        return;
      }

      tagged++;
      const show = (tag === 'all' || tag === selected);
      setHidden(el, !show);
    });
    return tagged;
  }

  function applyFilter(root) {
    const selected = getSelectedColor(root) || getSelectedColor(document);
    if (!selected) return;

    const media = getMediaContainers(root);
    const thumbs = getThumbContainers(root);

    const taggedMedia = filterContainers(media, selected);
    const taggedThumbs = filterContainers(thumbs, selected);

    log('selected', selected, 'media', media.length, 'thumbs', thumbs.length, 'tagged', taggedMedia, taggedThumbs);

    if (taggedMedia + taggedThumbs === 0) return;
    refreshCarousels(root);
  }

  function applyNoFlash(root) {
    ensureFlashStyle();
    document.documentElement.classList.add('vmf-filtering');
    applyFilter(root);
    requestAnimationFrame(() => {
      applyFilter(root);
      document.documentElement.classList.remove('vmf-filtering');
    });
  }

  function isColorControl(t) {
    if (!t) return false;
    if (!(t.matches?.('select, input[type="radio"]'))) return false;

    const name = (t.getAttribute('name') || '');
    if (name === 'options[' + OPTION_NAME + ']') return true;
    if (name.includes('options[' + OPTION_NAME + ']')) return true;
    if (name.toLowerCase().includes('color')) return true;
    return false;
  }

  function bind() {
    log('Initialized variant-media-filter');
    applyNoFlash(document);

    document.addEventListener('change', (e) => {
      const t = e.target;
      if (!isColorControl(t)) return;
      applyNoFlash(getRootFromTarget(t));
    });

    document.addEventListener('variant:change', (e) => {
      applyNoFlash(getRootFromTarget(e.target));
    });

    document.addEventListener('product-info:loaded', (e) => {
      applyNoFlash(getRootFromTarget(e.target));
    });

    if (window.pubsub && typeof window.pubsub.subscribe === 'function' && window.PUB_SUB_EVENTS) {
      window.pubsub.subscribe(window.PUB_SUB_EVENTS.variantChange, (event) => {
        applyNoFlash(document);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
})();
